import { Loader2 } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  type GetSubmissionAttemptDetailResponse,
  type SubmitSubmissionAttemptRequest,
  type SubmitSubmissionAttemptResponse,
  useRebuildSubmissionAttemptMutation,
  useSubmitSubmissionAttemptMutation,
} from '@/services/api/agentflowService';
import { firmsApiClient } from '@/services/api/firmsService';
import { mattersApiClient } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import {
  getSubmissionAttemptPresentationState,
  getSubmissionAttemptRebuildHelperText,
  getSubmissionAttemptSummary,
} from './submissionAttemptPresentation';

type FirmFrontChannel = {
  address?: string | null;
  front_id?: string | null;
};

interface SubmissionAttemptActionsProps {
  detail: GetSubmissionAttemptDetailResponse;
  matterId: string;
  requestId: string;
  submissionPhase: 'idle' | 'submitting';
  onSubmissionPhaseChange: (phase: 'idle' | 'submitting') => void;
  onClose: () => void;
  onSubmitted: (response: SubmitSubmissionAttemptResponse) => void;
  onOpenRequestModal: () => void;
}

function getApiErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if ('data' in error && error.data && typeof error.data === 'object') {
    const { data } = error;
    if ('error' in data && typeof data.error === 'string') {
      return data.error;
    }
    if ('message' in data && typeof data.message === 'string') {
      return data.message;
    }
  }

  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return null;
}

function getSubmitButtonLabel(
  submissionPhase: 'idle' | 'submitting',
  isSubmittingState: boolean,
): string {
  if (submissionPhase === 'submitting' || isSubmittingState) {
    return 'Submitting Package';
  }
  return 'Submit Package';
}

function getSubmitHelperText(
  submissionPhase: 'idle' | 'submitting',
  isSubmittingState: boolean,
): string {
  if (submissionPhase === 'submitting' || isSubmittingState) {
    return 'Sending the HIPAA package and moving the request into Confirm Receipt...';
  }
  return "Promotes this draft into the HIPAA package and starts submission with the agent's selections.";
}

function resolveSubmissionAttemptAssetUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }

  try {
    return new URL(
      url,
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST ?? window.location.origin,
    ).toString();
  } catch {
    return url;
  }
}

function getEmailTemplateErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if ('data' in error && error.data && typeof error.data === 'object') {
    const data = error.data as { detail?: string; message?: string; error?: string };
    return (
      data.detail ||
      data.message ||
      data.error ||
      'Unable to load the email template. You can still edit the message manually.'
    );
  }

  if ('message' in error && typeof error.message === 'string') {
    return (
      error.message ||
      'Unable to load the email template. You can still edit the message manually.'
    );
  }

  return 'Unable to load the email template. You can still edit the message manually.';
}

export const SubmissionAttemptActions = ({
  detail,
  matterId,
  requestId,
  submissionPhase,
  onSubmissionPhaseChange,
  onClose,
  onSubmitted,
  onOpenRequestModal,
}: SubmissionAttemptActionsProps) => {
  const [submitAttempt, { isLoading: isSubmitting }] =
    useSubmitSubmissionAttemptMutation();
  const [rebuild, { isLoading: isRebuilding }] =
    useRebuildSubmissionAttemptMutation();
  const [isEmailSubmitModalOpen, setIsEmailSubmitModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTemplateInitialized, setEmailTemplateInitialized] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);

  const activeContactMethod =
    detail.contact_method?.manual_contact_method ??
    detail.contact_method?.recommended_contact_method ??
    null;
  const isEmailContactMethod = activeContactMethod?.channel === 'email';
  const isMailContactMethod = activeContactMethod?.channel === 'mail';
  const downloadUrl = resolveSubmissionAttemptAssetUrl(detail.pdf_url);

  const {
    data: matterData,
    isLoading: isMatterLoading,
    isFetching: isMatterFetching,
    error: matterError,
  } = mattersApiClient.useGetMatterQuery(matterId, {
    skip: !isEmailContactMethod,
  });
  const firmId = matterData?.firm?.id;
  const {
    data: firmData,
    isLoading: isFirmLoading,
    isFetching: isFirmFetching,
    error: firmError,
  } = firmsApiClient.useGetFirmQuery(firmId ?? '', {
    skip: !firmId,
  });
  const firmFrontChannel = (firmData?.front_channel ?? null) as FirmFrontChannel | null;
  const hasUsableFrontChannel = Boolean(
    firmFrontChannel?.address || firmFrontChannel?.front_id,
  );
  const isFirmDataLoading =
    isEmailContactMethod &&
    (isMatterLoading ||
      isMatterFetching ||
      (Boolean(firmId) && (isFirmLoading || isFirmFetching)));
  const emailSubmitDisabledReason = (() => {
    if (!isEmailContactMethod) {
      return null;
    }
    if (isFirmDataLoading) {
      return 'Loading firm configuration...';
    }
    if (matterError || firmError) {
      return 'Unable to load firm configuration.';
    }
    if (!hasUsableFrontChannel) {
      return 'This firm does not have a Front inbox configured. Configure a firm Front channel before sending this request by email.';
    }
    return null;
  })();

  const {
    data: emailTemplate,
    isFetching: isEmailTemplateFetching,
    isLoading: isEmailTemplateLoading,
    isError: isEmailTemplateError,
    error: emailTemplateError,
  } = treatmentRecordRequestService.useGetHipaaEmailTemplateQuery(
    {
      matterId,
      providerId: detail.provider_id ?? '',
      requestId,
    },
    {
      skip: !isEmailSubmitModalOpen || !detail.provider_id,
    },
  );

  useEffect(() => {
    if (!isEmailSubmitModalOpen) {
      setEmailSubject('');
      setEmailBody('');
      setEmailValidationError(null);
      setEmailTemplateInitialized(false);
      return;
    }

    if (emailTemplate && !emailTemplateInitialized) {
      setEmailSubject(emailTemplate.subject);
      setEmailBody(emailTemplate.body);
      setEmailValidationError(null);
      setEmailTemplateInitialized(true);
      return;
    }

    if (isEmailTemplateError && !emailTemplateInitialized) {
      setEmailTemplateInitialized(true);
    }
  }, [
    emailTemplate,
    emailTemplateInitialized,
    isEmailSubmitModalOpen,
    isEmailTemplateError,
  ]);

  const handleOpenRequestModal = useCallback(() => {
    onClose();
    onOpenRequestModal();
  }, [onClose, onOpenRequestModal]);

  const handleSubmit = useCallback(
    async (body?: SubmitSubmissionAttemptRequest) => {
      onSubmissionPhaseChange('submitting');
      try {
        const response = await submitAttempt({ matterId, requestId, body }).unwrap();
        if (response.submission_attempt.status === 'submitted') {
          toast.success('Package submitted');
        } else {
          toast.info(response.message || 'Submission queued');
        }
        setIsEmailSubmitModalOpen(false);
        onSubmitted(response);
      } catch (error) {
        toast.error(getApiErrorMessage(error) ?? 'Failed to submit package');
      } finally {
        onSubmissionPhaseChange('idle');
      }
    },
    [matterId, onSubmissionPhaseChange, onSubmitted, requestId, submitAttempt],
  );

  const handleSubmitPackage = useCallback(async () => {
    if (isEmailContactMethod) {
      if (emailSubmitDisabledReason) {
        return;
      }
      setIsEmailSubmitModalOpen(true);
      return;
    }

    await handleSubmit();
  }, [emailSubmitDisabledReason, handleSubmit, isEmailContactMethod]);

  const handleConfirmEmailSubmit = useCallback(async () => {
    const trimmedSubject = emailSubject.trim();
    const trimmedBody = emailBody.trim();

    if (!trimmedSubject || !trimmedBody) {
      setEmailValidationError('Subject and body are required.');
      return;
    }

    if (!hasUsableFrontChannel) {
      setEmailValidationError(
        'A firm Front channel must be configured before this email can be sent.',
      );
      return;
    }

    setEmailValidationError(null);
    await handleSubmit({
      email_subject: trimmedSubject,
      email_body: trimmedBody,
      ...(firmFrontChannel?.address
        ? { sender_email: firmFrontChannel.address }
        : { front_channel_id: firmFrontChannel?.front_id ?? null }),
    });
  }, [
    emailBody,
    emailSubject,
    firmFrontChannel?.address,
    firmFrontChannel?.front_id,
    handleSubmit,
    hasUsableFrontChannel,
  ]);

  const handleRebuild = useCallback(async () => {
    try {
      await rebuild({ matterId, requestId }).unwrap();
      toast.success('Package rebuilt');
    } catch {
      toast.error('Failed to rebuild package');
    }
  }, [matterId, requestId, rebuild]);

  const isReady = detail.status === 'ready';
  const isSubmittingState = detail.status === 'submitting';
  const isSubmittingFlow = submissionPhase !== 'idle';
  const isBlocked =
    detail.status === 'blocked' ||
    detail.status === 'needs_rebuild' ||
    detail.status === 'needs_re_evaluation';
  const presentationState = getSubmissionAttemptPresentationState(detail);
  const summary = getSubmissionAttemptSummary(detail);
  const canRebuild = isBlocked && presentationState.manualInputCount === 0;
  const helperText = isBlocked ? getSubmissionAttemptRebuildHelperText(detail) : '';
  const showHelperText = isBlocked && Boolean(helperText);
  const submitButtonLabel = getSubmitButtonLabel(submissionPhase, isSubmittingState);
  const submitHelperText = getSubmitHelperText(submissionPhase, isSubmittingState);
  const isEmailTemplatePending =
    !emailTemplateInitialized && (isEmailTemplateLoading || isEmailTemplateFetching);
  const emailTemplateErrorMessage = useMemo(
    () => getEmailTemplateErrorMessage(emailTemplateError),
    [emailTemplateError],
  );
  const submitButtonDisabled =
    isSubmitting ||
    isSubmittingState ||
    isSubmittingFlow ||
    Boolean(emailSubmitDisabledReason);
  const submitButton = (
    <Button
      className={cn(
        'w-full gap-2',
        isSubmittingState &&
          'bg-blue-600 text-white hover:bg-blue-600 disabled:opacity-100',
      )}
      onClick={isReady ? handleSubmitPackage : undefined}
      disabled={submitButtonDisabled}
    >
      {(isSubmitting || isSubmittingFlow) && (
        <Loader2 className="h-4 w-4 animate-spin" />
      )}
      {submitButtonLabel}
    </Button>
  );
  const mailDownloadButton = downloadUrl ? (
    <Button asChild className="w-full gap-2">
      <a href={downloadUrl} download target="_blank" rel="noreferrer">
        Download PDF
      </a>
    </Button>
  ) : (
    <Button className="w-full gap-2" disabled>
      Download PDF
    </Button>
  );
  let primaryActionContent: ReactNode = submitButton;
  if (isMailContactMethod) {
    primaryActionContent = (
      <>
        {mailDownloadButton}
        <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
          Agent submission does not support mail. Download the package and handle mailing manually.
        </p>
      </>
    );
  } else if (emailSubmitDisabledReason) {
    primaryActionContent = (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full">{submitButton}</div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">
          {emailSubmitDisabledReason}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="border-t border-zinc-200 px-5 py-3 space-y-2 shrink-0">
      {(isReady || isSubmittingState) && (
        <>
          {primaryActionContent}
          {!isMailContactMethod && (
            <p className="text-[10px] text-zinc-500 text-center">
              {submitHelperText}
            </p>
          )}
        </>
      )}

      {detail.status === 'submitted' && (
        <>
          <Button className="w-full" disabled>
            Package Submitted
          </Button>
          <p className="text-[10px] text-zinc-500 text-center">
            This package has already been submitted.
          </p>
        </>
      )}

      {isBlocked && (
        <>
          <Button
            className="w-full gap-2"
            variant={canRebuild ? 'default' : 'outline'}
            onClick={handleRebuild}
            disabled={!canRebuild || isRebuilding}
          >
            {isRebuilding && <Loader2 className="h-4 w-4 animate-spin" />}
            Rebuild Package
          </Button>
          {showHelperText && (
            <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
              {helperText}
            </p>
          )}
          {presentationState.reviewBlocked && summary.description && (
            <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
              Non-blocking blank fields shown above were left blank intentionally.
            </p>
          )}
        </>
      )}

      <button
        type="button"
        className="w-full text-center text-xs font-medium text-zinc-500 hover:text-zinc-700 py-1"
        onClick={handleOpenRequestModal}
        disabled={isSubmittingFlow || isSubmittingState}
      >
        Configure &amp; submit manually instead
      </button>

      <Dialog
        open={isEmailSubmitModalOpen}
        onOpenChange={(nextOpen) => {
          if ((isSubmitting || isSubmittingFlow) && !nextOpen) {
            return;
          }
          setIsEmailSubmitModalOpen(nextOpen);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Email Submission</DialogTitle>
            <DialogDescription>
              Confirm the sender, subject, and message before the package is submitted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {isEmailTemplatePending ? (
              <div className="flex items-center justify-center gap-3 py-8 text-sm text-zinc-600">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading email template...</span>
              </div>
            ) : (
              <>
                {emailTemplateErrorMessage && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {emailTemplateErrorMessage}
                  </div>
                )}

                {emailValidationError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {emailValidationError}
                  </div>
                )}

                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor="submission-attempt-email-to"
                    className="text-sm font-medium text-zinc-900"
                  >
                    To
                  </label>
                  <Input
                    id="submission-attempt-email-to"
                    value={activeContactMethod?.value ?? 'No email configured'}
                    disabled
                    className="bg-zinc-50 text-zinc-700"
                  />
                </div>

                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor="submission-attempt-email-from"
                    className="text-sm font-medium text-zinc-900"
                  >
                    From
                  </label>
                  <Input
                    id="submission-attempt-email-from"
                    value={
                      firmFrontChannel?.address ??
                      (firmFrontChannel?.front_id
                        ? 'Configured Front inbox'
                        : 'Firm Front channel is not configured')
                    }
                    disabled
                    className="bg-zinc-50 text-zinc-700"
                  />
                </div>

                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor="submission-attempt-email-subject"
                    className="text-sm font-medium text-zinc-900"
                  >
                    Subject
                  </label>
                  <Input
                    id="submission-attempt-email-subject"
                    value={emailSubject}
                    onChange={(event) => setEmailSubject(event.target.value)}
                    placeholder="Enter email subject"
                  />
                </div>

                <div className="space-y-2">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    htmlFor="submission-attempt-email-body"
                    className="text-sm font-medium text-zinc-900"
                  >
                    Message
                  </label>
                  <Textarea
                    id="submission-attempt-email-body"
                    value={emailBody}
                    onChange={(event) => setEmailBody(event.target.value)}
                    rows={12}
                    className="min-h-[260px]"
                    placeholder="Enter email message"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEmailSubmitModalOpen(false)}
              disabled={isSubmitting || isSubmittingFlow}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmEmailSubmit}
              disabled={isSubmitting || isSubmittingFlow || isEmailTemplatePending}
            >
              {(isSubmitting || isSubmittingFlow) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
